import type {
  Destination,
  OriginContext,
} from '@web-platform/travel-tools-flight-price-map-types/src/ubff/travel-confidence-map';

export type Bounds = { north: number; south: number; east: number; west: number };

type HasCoordinates = { coordinates?: { latitude: number; longitude: number } };

const INITIAL_ZOOM_RADIUS_DESKTOP = 12;
const INITIAL_ZOOM_RADIUS_MOBILE = 6;

export const MAP_REFRESH_VECTOR_ID = '1b37fd270a07f036';

export const clamp = (value: number, min: number, max: number) =>
  Math.max(min, Math.min(max, value));

export const getInitialBounds = (
  markers: HasCoordinates[],
  fallback: Bounds,
  isMobile = false,
): Bounds => {
  const first = markers[0];
  if (!first?.coordinates) return fallback;
  const { latitude, longitude } = first.coordinates;
  const radius = isMobile ? INITIAL_ZOOM_RADIUS_MOBILE : INITIAL_ZOOM_RADIUS_DESKTOP;
  return {
    north: clamp(latitude + radius, -90, 90),
    south: clamp(latitude - radius, -90, 90),
    east: clamp(longitude + radius, -180, 180),
    west: clamp(longitude - radius, -180, 180),
  };
};

export type CityItem = {
  entityId: string;
  name: string;
};

export const getOriginName = (originContext?: OriginContext, cityEntityId?: string): string => {
  if (!originContext) return '';

  if (cityEntityId) {
    const match = originContext.cities?.find((city) => city.entityId === cityEntityId);
    if (match?.name) return match.name;
  }

  return originContext.countryName ?? '';
};

export const getOriginCities = (originContext?: OriginContext): CityItem[] =>
  (originContext?.cities ?? []).filter((c): c is CityItem => Boolean(c.entityId && c.name));

const normalise = (value: string) => value.trim().toLowerCase();

const FLIGHT_PRICE_MAP_PATHNAME = '/travel-tools/flight-price-map';

export const getFlightPriceMapCityUrl = (
  countrySlug: string,
  cityEntityId: string,
  search = '',
): string => `${FLIGHT_PRICE_MAP_PATHNAME}/${countrySlug}/ci-${cityEntityId}${search}`;

export const getFlightPriceMapCountryUrl = (search = ''): string =>
  `${FLIGHT_PRICE_MAP_PATHNAME}${search}`;

export const matchesCountryName = (searchQuery: string, countryName?: string): boolean => {
  const query = normalise(searchQuery);
  if (!countryName || !query) return false;
  return normalise(countryName).includes(query);
};

export const filterOriginCities = ({
  cities,
  countryName,
  searchQuery,
}: {
  cities: CityItem[];
  countryName?: string;
  searchQuery: string;
}): CityItem[] => {
  const query = normalise(searchQuery);
  if (!query || matchesCountryName(searchQuery, countryName)) {
    return cities;
  }

  return cities.filter(({ name }) => normalise(name).includes(query));
};

export const filterForDestinations = (destinations: Destination[], searchQuery: string) => {
  const query = normalise(searchQuery);
  if (!query) {
    return destinations;
  }

  return destinations.filter(({ cityName, countryName }) => {
    const searchableText = normalise(`${cityName},${countryName}`);
    return searchableText.includes(query);
  });
};
